/**
 * 시나리오의 모델 참조 — 동작의 `modelId` 는 **최상위 저작 키(AUTHORED_PROJECT_KEYS) 가운데 모델 id 를 담는 유일한 것**이다
 * (`coSimConfig`·`nodeSizingRules` 는 참가자를 종류·이름으로만 가리킨다; 모델 안의 `RestartSource.modelId` 는 자기 `projectId` 를
 * 함께 가져 이 프로젝트의 저작 키가 아니다). 모델 id 가 새로 찍히는 자리(JSON 가져오기, 예제 설치의 제자리 갱신)와 id 를 빼고 대조하는
 * 자리(예제 설치기의 «같음» 판정)가 이 한 곳을 쓴다 — 시나리오가 모델을 가리키는 방식이 바뀌면 여기만 바뀐다.
 */
import type { Model } from '@/types/project';
import type { Scenario } from './schema';

/** 동작의 모델 id 를 대조표로 바꾼다 — 표에 없는 id 는 그대로(프로젝트에 없는 모델은 시작 게이트가 사유로 거절한다) */
export function remapScenarioModelIds(scenarios: readonly Scenario[], idMap: ReadonlyMap<string, string>): Scenario[] {
  return scenarios.map((s) => ({
    ...s,
    actions: s.actions.map((a) => ({ ...a, modelId: idMap.get(a.modelId) ?? a.modelId })),
  }));
}

/** 옛 모델 목록 → 새 모델 목록의 id 대조표(이름이 같은 모델은 같은 모델) */
export function modelIdMapByName(from: readonly Pick<Model, 'id' | 'name'>[], to: readonly Pick<Model, 'id' | 'name'>[]): Map<string, string> {
  const byName = new Map(to.map((m) => [m.name, m.id]));
  const out = new Map<string, string>();
  for (const m of from) {
    const id = byName.get(m.name);
    if (id !== undefined) out.set(m.id, id);
  }
  return out;
}

/**
 * id 를 빼고 대조하는 꼴 — 동작의 `modelId` 를 그 모델의 **이름**으로 바꾼다. 예제 템플릿은 만들 때마다 모델 id 를 새로
 * 찍으므로 id 그대로 비교하면 손대지 않은 예제도 늘 «다름» 이다. 프로젝트에 없는 id 는 그대로 남아 차이로 드러난다.
 */
export function scenariosWithModelNames(scenarios: readonly Scenario[] | undefined, models: readonly Pick<Model, 'id' | 'name'>[] | undefined): Scenario[] | null {
  if (!scenarios) return null;
  const nameOf = new Map((models ?? []).map((m) => [m.id, m.name]));
  return remapScenarioModelIds(scenarios, nameOf);
}
